import { ComputationType } from "@/graphql/__generated__/graphql";

/**
 * Configuration object for computation types that defines behavior and formatting
 * @interface ComputationTypeConfig
 * @property {boolean} [isPercentage] - Whether the value should be formatted as a percentage
 * @property {boolean} [isCurrency] - Whether the value should be formatted as currency
 * @property {boolean} [hasMonthSuffix] - Whether to append "months" to the displayed value
 * @property {boolean} [isStringInput] - Whether the input should accept string values
 * @property {boolean} [allowNull] - Whether null values are allowed
 * @property {boolean} [renderInBidRule] - Whether this type can be used in bid rules
 * @property {boolean} [allowNullComparator] - Whether null comparisons are allowed
 */
export interface ComputationTypeConfig {
  isPercentage?: boolean;
  isCurrency?: boolean;
  hasMonthSuffix?: boolean;
  isStringInput?: boolean;
  allowNull?: boolean;
  renderInBidRule?: boolean;
  allowNullComparator?: boolean;
}

export const COMPUTATION_TYPE_CONFIG: Record<ComputationType, ComputationTypeConfig> = {
  [ComputationType.PermanentMargin]: {
    isPercentage: true,
  },
  [ComputationType.MobileMargin]: {
    isPercentage: true,
  },
  [ComputationType.TotalMargin]: {
    isPercentage: true,
  },
  [ComputationType.TotalRevenue]: {
    isCurrency: true,
  },
  [ComputationType.EstimatedRevenue]: {
    isCurrency: true,
    renderInBidRule: true,
  },
  [ComputationType.ContractDuration]: {
    hasMonthSuffix: true,
    renderInBidRule: true,
  },
  [ComputationType.EscalationClause]: {
    isPercentage: true,
    renderInBidRule: true,
    allowNull: true,
    allowNullComparator: true,
  },
  [ComputationType.NonTariffAllowance]: {
    isCurrency: true,
  }
};

/**
 * Helper functions to check properties of computation types
 */
export function isPercentageType(type: ComputationType): boolean {
  return !!COMPUTATION_TYPE_CONFIG[type]?.isPercentage;
}

export function isCurrencyType(type: ComputationType): boolean {
  return !!COMPUTATION_TYPE_CONFIG[type]?.isCurrency;
}

export function hasMonthSuffix(type: ComputationType): boolean {
  return !!COMPUTATION_TYPE_CONFIG[type]?.hasMonthSuffix;
}

export function isStringInputType(type: ComputationType): boolean {
  return !!COMPUTATION_TYPE_CONFIG[type]?.isStringInput;
}

export function allowsNullValue(type: ComputationType): boolean {
  return !!COMPUTATION_TYPE_CONFIG[type]?.allowNull;
}

export function isAllowedInBidRules(type: ComputationType): boolean {
  return !!COMPUTATION_TYPE_CONFIG[type]?.renderInBidRule;
}

export function shouldAllowNullComparator(type: ComputationType): boolean {
  return !!COMPUTATION_TYPE_CONFIG[type]?.allowNullComparator;
}

/**
 * For backward compatibility, generate the original arrays from the config
 */
export const percentageInputKeys = Object.entries(COMPUTATION_TYPE_CONFIG)
  .filter(([_, config]) => config.isPercentage)
  .map(([key]) => key as ComputationType);

export const currencyInputKeys = Object.entries(COMPUTATION_TYPE_CONFIG)
  .filter(([_, config]) => config.isCurrency)
  .map(([key]) => key as ComputationType);

export const bidComputationTypes = Object.entries(COMPUTATION_TYPE_CONFIG)
  .filter(([_, config]) => config.renderInBidRule)
  .map(([key]) => key as ComputationType);

export const monthSuffixComputationTypes = Object.entries(COMPUTATION_TYPE_CONFIG)
  .filter(([_, config]) => config.hasMonthSuffix)
  .map(([key]) => key as ComputationType);

export const stringInputKeys = Object.entries(COMPUTATION_TYPE_CONFIG)
  .filter(([_, config]) => config.isStringInput)
  .map(([key]) => key as ComputationType);

export const allowNullInputKeys = Object.entries(COMPUTATION_TYPE_CONFIG)
  .filter(([_, config]) => config.allowNull)
  .map(([key]) => key as ComputationType);

/**
 * Array of strings that will not be rendered in the condition component
 * @type {string[]}
 */
export const listOfStringsToNotRender = [
  "null"
];
